import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { Sequence, SoftwareType, HolidayType } from '@/types/sequence';
import { useSequenceFilter } from '@/hooks/useSequenceFilter';
import { SequenceFiltersSidebar } from '@/components/sequences/SequenceFiltersSidebar';
import { SequenceCard } from '@/components/sequences/SequenceCard';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { 
  holidays,
  genres,
  props,
  popularCombinations,
  sequenceTypes as marketplaceSequenceTypes,
  generatePageTitle,
  generateMetaDescription
} from '@/data/marketplace';
import { CategoryTile } from '@/components/marketplace/CategoryTile';
import { FeaturedSequencer } from '@/components/marketplace/FeaturedSequencer';
import { SEOFooter } from '@/components/marketplace/SEOFooter';

// Mock sequence categories
const categories = [
  { id: 'all', name: 'All Sequences' },
  { id: 'free', name: 'Free Sequences' },
  { id: 'xLights', name: 'xLights' },
  { id: 'LOR', name: 'LOR' },
  { id: 'popular', name: 'Most Popular' },
  { id: 'recent', name: 'Recently Added' },
];

// We'll get genres dynamically from the real data

const primaryCategories = [
  {
    title: 'Christmas',
    imageSrc: '/images/categories/christmas.jpg',
    altText: 'Christmas light sequences with decorated trees and houses',
    href: '/search?holiday=Christmas',
    featured: true
  },
  {
    title: 'Halloween',
    imageSrc: '/images/categories/halloween.jpg',
    altText: 'Spooky Halloween light sequences',
    href: '/search?holiday=Halloween'
  },
  {
    title: '4th of July',
    imageSrc: '/images/categories/fourth-july.jpg',
    altText: 'Patriotic light sequences for Independence Day',
    href: '/search?holiday=4thOfJuly'
  }
];

const featuredSequencers = [
  {
    name: 'John Doe',
    avatar: '/avatars/john.jpg',
    stats: { views: 50000, uploads: 25 },
    previewImageUrl: '/previews/john.jpg',
    profileUrl: '/users/john'
  },
  // Add more featured sequencers
];

const seoFooterLinks = {
  holidays: holidays.map(holiday => ({
    title: `${holiday.title} Light Sequences`,
    href: `/search?holiday=${holiday.id}`
  })),
  genres: genres.map(genre => ({
    title: `${genre.title} Music Light Shows`,
    href: `/search?genre=${genre.id}`
  })),
  props: props.map(prop => ({
    title: `${prop.title} Displays`,
    href: `/search?prop=${prop.id}`
  })),
  combinations: popularCombinations.map(combo => ({
    title: combo.title,
    href: combo.href
  }))
};

const Sequences = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { software, holiday } = useParams();
  const pageTitle = generatePageTitle({ holiday });
  const metaDescription = generateMetaDescription({ holiday });

  // Use the proper sequence filter hook for real Supabase data
  const { sequences, isLoading, filters } = useSequenceFilter();
  
  // Keep some local state for UI components that aren't fully integrated with the hook yet
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedSoftware, setSelectedSoftware] = useState<SoftwareType[]>([]);
  const [selectedHolidays, setSelectedHolidays] = useState<HolidayType[]>([]);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedArtists, setSelectedArtists] = useState<string[]>([]);
  const [selectedAlbums, setSelectedAlbums] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 100]);
  const [artistCounts, setArtistCounts] = useState<{ name: string; count: number }[]>([]);
  const [albumCounts, setAlbumCounts] = useState<{ name: string; count: number }[]>([]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    
    // Update parameters only if they've changed from their current values
    const currentSoftware = params.get('software')?.split(',') || [];
    const currentHoliday = params.get('holiday')?.split(',') || [];
    
    if (selectedSoftware.length > 0) {
      if (!arrayEquals(selectedSoftware, currentSoftware)) {
        params.set('software', selectedSoftware.join(','));
      }
    } else {
      params.delete('software');
    }
    
    if (selectedHolidays.length > 0) {
      if (!arrayEquals(selectedHolidays, currentHoliday)) {
        params.set('holiday', selectedHolidays.join(','));
      }
    } else {
      params.delete('holiday');
    }
    
    if (selectedGenres.length > 0) {
      params.set('genres', selectedGenres.join(','));
    } else {
      params.delete('genres');
    }
    
    if (selectedArtists.length > 0) {
      params.set('artists', selectedArtists.join(','));
    } else {
      params.delete('artists');
    }
    
    if (selectedAlbums.length > 0) {
      params.set('albums', selectedAlbums.join(','));
    } else {
      params.delete('albums');
    }
    
    if (searchQuery) {
      params.set('search', searchQuery);
    } else {
      params.delete('search');
    }
    
    if (priceRange[0] > 0) {
      params.set('minPrice', priceRange[0].toString());
    } else {
      params.delete('minPrice');
    }
    
    if (priceRange[1] < 100) {
      params.set('maxPrice', priceRange[1].toString());
    } else {
      params.delete('maxPrice');
    }
    
    const search = params.toString();
    if (search !== searchParams.toString()) {
      navigate({ search }, { replace: true });
    }
  }, [selectedSoftware, selectedHolidays, selectedGenres, selectedArtists, selectedAlbums, searchQuery, priceRange, navigate, searchParams]);

  // Helper function to compare arrays
  const arrayEquals = (a: string[], b: string[]) => {
    return a.length === b.length && a.every((val, index) => val === b[index]);
  };

  // Update artist and album counts based on real data from Supabase
  useEffect(() => {
    if (sequences && sequences.length > 0) {
      // For now, we'll use placeholder counts since the real data structure might be different
      // This will need to be updated based on how artist/genre data is stored in the actual Supabase schema
      setArtistCounts([]);
      setAlbumCounts([]);
    }
  }, [sequences]);

  // Handler functions for state updates
  const handleSoftwareChange = (software: SoftwareType) => {
    setSelectedSoftware(prev => prev.includes(software) ? prev.filter(s => s !== software) : [...prev, software]);
  };

  const handleHolidayChange = (holiday: HolidayType) => {
    setSelectedHolidays(prev => prev.includes(holiday) ? prev.filter(h => h !== holiday) : [...prev, holiday]);
  };

  const handleGenreChange = (genre: string) => {
    setSelectedGenres(prev => prev.includes(genre) ? prev.filter(g => g !== genre) : [...prev, genre]);
  };

  const handleArtistChange = (artist: string) => {
    setSelectedArtists(prev => prev.includes(artist) ? prev.filter(a => a !== artist) : [...prev, artist]);
  };

  const handleAlbumChange = (album: string) => {
    setSelectedAlbums(prev => prev.includes(album) ? prev.filter(a => a !== album) : [...prev, album]);
  };

  const handlePriceChange = (value: [number, number]) => {
    setPriceRange(value);
  };

  const clearFilters = () => {
    setSelectedSoftware([]);
    setSelectedHolidays([]);
    setSelectedGenres([]);
    setSelectedArtists([]);
    setSelectedAlbums([]);
    setPriceRange([0, 100]);
    setSearchQuery('');
    // Clear URL parameters
    navigate({ search: '' }, { replace: true });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?keyword=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
      </Helmet>

      <div className="min-h-screen flex flex-col">
        <NavBar />
        
        <main className="flex-1">
          {/* Fixed Search Bar */}
          <div className="sticky top-16 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
            <div className="container py-4">
              <form onSubmit={handleSearch} className="mx-auto max-w-2xl">
                <div className="relative flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="search"
                      placeholder="Search songs, props, genres or artists..."
                      className="pl-10"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <Button type="submit" className="shrink-0">
                    Search
                  </Button>
                </div>
              </form>
            </div>
          </div>

          {/* Content Area with Extra Padding */}
          <div className="pt-32">
            {/* Page Title - Below search bar with proper spacing */}
            <section className="container py-8">
              <h1 className="text-4xl font-bold text-center">Find Your Perfect Light Show Sequence</h1>
            </section>

            {/* Featured Holidays Section */}
            <section className="container mb-12">
              <h2 className="mb-6 text-2xl font-semibold">Featured Holidays</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {primaryCategories.map((category) => (
                  <CategoryTile
                    key={category.title}
                    title={category.title}
                    imageSrc={category.imageSrc}
                    altText={category.altText}
                    href={category.href}
                    featured={category.featured}
                  />
                ))}
              </div>
            </section>

            {/* Browse by Genre Section */}
            <section className="container mb-12">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-2xl font-semibold">Browse by Genre</h2>
                <a href="/sequences/genres" className="text-primary font-medium hover:underline text-sm">Browse all genres &rarr;</a>
              </div>
              <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-4">
                {genres.map((genre) => {
                  const Icon = genre.icon;
                  return (
                    <CategoryTile
                      key={genre.id}
                      title={genre.title}
                      icon={Icon}
                      altText={genre.altText}
                      href={`/search?genre=${genre.id}`}
                    />
                  );
                })}
              </div>
            </section>

            {/* Sequence Types Section */}
            <section className="container mb-12">
              <h2 className="mb-6 text-2xl font-semibold">Sequence Types</h2>
              <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-4">
                {marketplaceSequenceTypes.map((type) => {
                  const Icon = type.icon;
                  return (
                    <CategoryTile
                      key={type.id}
                      title={type.title}
                      icon={Icon}
                      altText={type.altText}
                      href={`/search?type=${type.id}`}
                    />
                  );
                })}
              </div>
            </section>

            {/* New Releases Section */}
            <section className="container mb-12">
              <h2 className="mb-6 text-2xl font-semibold">New Releases</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {sequences
                  .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                  .slice(0, 8)
                  .map((sequence) => (
                    <SequenceCard key={sequence.id} sequence={sequence} />
                  ))}
              </div>
            </section>

            {/* Popular Section */}
            <section className="container mb-12">
              <h2 className="mb-6 text-2xl font-semibold">Popular Sequences</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {sequences
                  .slice(0, 8)
                  .map((sequence) => (
                    <SequenceCard key={sequence.id} sequence={sequence} />
                  ))}
              </div>
            </section>

            {/* Main Content: Filters and Results */}
            <section className="container mb-16">
              <div className="flex gap-8">
                {/* Filters Sidebar */}
                <aside className="hidden md:block w-64 sticky top-32">
                  <SequenceFiltersSidebar
                    selectedSoftware={selectedSoftware}
                    selectedHolidays={selectedHolidays}
                    selectedGenres={selectedGenres}
                    selectedArtists={selectedArtists}
                    selectedAlbums={selectedAlbums}
                    priceRange={priceRange}
                    allGenres={[]} // Will be populated with real genre data later
                    artists={artistCounts}
                    albums={albumCounts}
                    onSoftwareChange={handleSoftwareChange}
                    onHolidayChange={handleHolidayChange}
                    onGenreChange={handleGenreChange}
                    onArtistChange={handleArtistChange}
                    onAlbumChange={handleAlbumChange}
                    onPriceChange={handlePriceChange}
                    clearFilters={clearFilters}
                  />
                </aside>

                {/* Sequence Results */}
                <div className="flex-1">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {sequences.map((sequence) => (
                      <SequenceCard key={sequence.id} sequence={sequence} />
                    ))}
                  </div>
                  {sequences.length === 0 && !isLoading && (
                    <div className="text-center text-gray-500 mt-8">
                      No sequences found matching your criteria
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* SEO Footer */}
            <SEOFooter links={seoFooterLinks} />
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Sequences;
